import breadcrumbs from 'assets/img/breadcrumbs.svg';
import classNames from 'classnames';
import Button from 'components/Button/Button';
import LoaderWithProps from 'components/Loader/LoaderWithProps';
import { getFilePathForMediaFilesWithSizeForStyleSettings } from 'backend/additionalInformation';
import { useBlobDownload } from 'hooks/useBlobDownload';
import {
	StyleSettingsImageUploaderContext,
	IStyleSettingsImageUploaderContext,
} from '../styleSettingsImageUploaderState/styleSettingsImageUploaderContext';
import Text from 'components/Text/Text';
import React, { useContext, useEffect, useState } from 'react';
import style from './Uploader.module.scss';

interface IUploader {
	filePath: string | null;
	translationId: string;
	noImageIconId?: string;
	isNeedImgHeading?: boolean;
	itemKey: string | null;
	groupLoading: boolean;
	isMakeImageRequest: boolean;
	deleteFromNormalDisplayHandler: () => void;
	isDisabledControls?: boolean;
}

const Uploader: React.FC<IUploader> = ({
	filePath,
	translationId,
	noImageIconId = 'twoHills',
	isNeedImgHeading = false,
	itemKey,
	groupLoading,
	isMakeImageRequest,
	deleteFromNormalDisplayHandler,
	isDisabledControls,
}) => {
	const {
		changeTypeOfModalWindow,
		setCropperSrc,
		setUploaderSrc,
		setLoadingImageError,
		setLoading,
		uploaderSrc,
		loadingImageError,
		isLoading,
		isNewImageUploaded,
	} = useContext<IStyleSettingsImageUploaderContext>(StyleSettingsImageUploaderContext);
	const [isShowImage, setIsShowImage] = useState<boolean>(false);
	const downloadBlob = useBlobDownload({
		filePath,
		fileExtension: filePath?.split('.').pop() === 'gif' ? 'gif' : 'png',
		fileName: 'styleImage',
	});

	const deleteIconClasses = classNames(style.icon, style.deleteIcon);
	const deleteButtonClasses = classNames(style.deleteButton, {
		[style.buttonVisible]: uploaderSrc,
	});

	useEffect(() => {
		if (!isShowImage && isMakeImageRequest) {
			setIsShowImage(true);
		}
	}, [isMakeImageRequest]);

	useEffect(() => {
		const getImagePath = async () => {
			setLoading(true);
			const response = await getFilePathForMediaFilesWithSizeForStyleSettings(translationId, 'Medium');
			setUploaderSrc(response.data.preSignedUrl);
			setLoading(false);
		};

		isShowImage && translationId && filePath && getImagePath();
	}, [isShowImage]);

	const openCropper = () => {
		changeTypeOfModalWindow('cropper');
	};

	const deletePhoto = () => {
		setUploaderSrc(null);
		setCropperSrc(null);
		setLoadingImageError(false);
		deleteFromNormalDisplayHandler();
	};

	const showErrorImage = () => {
		setLoadingImageError(true);
	};

	const renderImage = () => {
		if (isLoading && !groupLoading) {
			return (
				<div className={style.imageContainer}>
					<LoaderWithProps loaderSize="big" />;
				</div>
			);
		}

		if (loadingImageError) {
			return (
				<div className={style.brokenImageContainer}>
					<svg className={style.brokenImageIcon}>
						<use xlinkHref={`${breadcrumbs}#brokenPicture`} />
					</svg>
					<div>Error image</div>
				</div>
			);
		}

		if (!uploaderSrc) {
			return (
				<div className={style.noImageContainer}>
					<svg className={style.noImageIcon}>
						<use xlinkHref={`${breadcrumbs}#${noImageIconId}`} />
					</svg>
				</div>
			);
		}

		if (uploaderSrc) {
			return (
				<div className={style.imageContainer}>
					{filePath && !isNewImageUploaded && (
						<div className={style.downloadOverlay} onClick={downloadBlob}>
							<svg className={style.downloadIcon}>
								<use xlinkHref={`${breadcrumbs}#downloadArrow`} />
							</svg>
						</div>
					)}
					<img className={style.image} src={uploaderSrc} alt="Photo" onError={showErrorImage} />
				</div>
			);
		}
	};

	return (
		<div className={style.container}>
			{isNeedImgHeading && (
				<Text
					className={style.itemKey}
					translationNameSpace="mediaSettings"
					translationKey={itemKey}
					weight="bold"
				/>
			)}
			{renderImage()}
			<div className={style.buttonsWrapper}>
				<Button
					icon={
						<svg className={style.icon}>
							<use xlinkHref={`${breadcrumbs}#${!uploaderSrc ? 'uploadPhoto' : 'reload'}`} />
						</svg>
					}
					disabled={isDisabledControls}
					onClick={openCropper}
					data-test="uploadReloadButton">
					<Text
						translationNameSpace="form"
						translationKey={!uploaderSrc ? 'uploadButton' : 'replaceButton'}
						asFragment={true}
					/>
				</Button>
				<Button
					className={deleteButtonClasses}
					theme="secondary"
					icon={
						<svg className={deleteIconClasses}>
							<use xlinkHref={`${breadcrumbs}#bin`} />
						</svg>
					}
					disabled={isDisabledControls}
					onClick={deletePhoto}
					data-test="deleteButton">
					<Text translationNameSpace="form" translationKey="deleteButton" asFragment={true} />
				</Button>
			</div>
		</div>
	);
};

export default Uploader;
